import {
  CheckSquare,
  ClipboardCheck,
  Clock,
  User,
} from "@tamagui/lucide-icons";
import React, { useContext, useEffect } from "react";
import type { ImageSourcePropType } from "react-native";
import { View } from "tamagui";

import AIDoctorIntakeFlow from "@/ai-doctor/AIDoctorIntakeFlow";
import { useCreateRxEligibilityConversation } from "@/api/generated";
import { WorkflowType } from "@/api/generated/models";
import {
  EncounterStartType,
  TextSendMessageRequestType as SendMessageRequestType,
} from "@/api/model";
import {
  usePostNoriMessage,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { useMergeStatus } from "@/api/queries";
import ConversationText2Outline from "@/assets/icons/ConversationText2Outline";
import DoctorOutline from "@/assets/icons/DoctorOutline";
import HealthcareOutline from "@/assets/icons/HealthcareOutline";
import IdeaOutline from "@/assets/icons/IdeaOutline";
import PillBottleWithPill from "@/assets/icons/PillBottleWithPill";
import AppImage from "@/assets/images/ai-doctor/woman-using-app.png";
import GLP1Hero from "@/assets/images/landing_page/v3/glp1-hero.png";
import MenopauseHero from "@/assets/images/landing_page/v3/menopause-hero.png";
import SymptomCheckingHero from "@/assets/images/landing_page/v3/symptom-checking-hero.png";
import TalkToDoctorHero from "@/assets/images/landing_page/v3/talk-to-doctor-hero.png";
import UTIReliefHero from "@/assets/images/landing_page/v3/uti-relief-hero.png";
import {
  Button,
  Image,
  LayoutContext,
  Logo,
  Section,
  Square,
  Text,
  XStack,
  YStack,
  useImageAspectRatio,
  useTheme,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { Link, Paragraph } from "@/components/Typography";
import {
  VISIT_STATUS,
  useVisitStatus,
} from "@/components/VisitStatusIndicator";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import CuraiConsentRedirect from "@/conversation/sidebar/CuraiConsentRedirect";
import WaitingRoom from "@/conversation/sidebar/WaitingRoom";
import VideoPreview from "@/conversation/video/VideoPreview";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import type { UseCase } from "@/hooks/suggestions";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useWaitTimeBanner } from "@/hooks/useWaitTimeBanner";
import WaitTimeBanner from "@/landing-page/general-care/WaitTimeBanner";
import { useRxQuestionnaireStore } from "@/store";

type HeroWithCTAItem = {
  Icon: React.ComponentType<{
    color: string;
    height: number;
    width: number;
    style?: any;
    flexShrink: number;
  }>;
  copyInPrimaryColor: string;
  copyInBrandColor: string;
};

export type HeroWithCTAProps = {
  header: React.ReactNode;
  ctaCopy: string;
  heroImage: ImageSourcePropType;
  items: HeroWithCTAItem[];
  onPress: () => void;
  disabled: boolean;
  dataTrackingId?: string;
  secondaryCtaCopy?: string;
  onSecondaryPress?: () => void;
  secondaryDataTrackingId?: string;
  banner?: React.ReactNode;
  // Small explanatory line under the CTA, e.g. why it's disabled.
  ctaHelperText?: string;
};

const HeroWithCTA = ({
  header,
  ctaCopy,
  onPress,
  disabled,
  heroImage,
  items,
  dataTrackingId,
  secondaryCtaCopy,
  onSecondaryPress,
  secondaryDataTrackingId,
  banner,
  ctaHelperText,
}: HeroWithCTAProps) => {
  const theme = useTheme();
  const iconColor = theme.iconBase?.get();
  const { lg, setShowHeader, headerHeight } = useContext(LayoutContext);

  useEffect(() => {
    setShowHeader(lg);
    return () => {
      setShowHeader(true);
    };
  }, [lg, setShowHeader]);

  const { aspectRatio: heroAspectRatio, onLoad: onHeroLoad } =
    useImageAspectRatio();

  const points = (
    <Section marginBottom="$400" $lg={{ marginBottom: "$600" }}>
      <Text
        paddingTop="$200"
        paddingBottom="$125"
        bold
        $lg={{ paddingTop: "$600", size: "$xl", bold: true }}
      >
        How it works:
      </Text>
      {items.map(({ copyInPrimaryColor, copyInBrandColor, Icon }) => (
        <XStack key={copyInPrimaryColor} alignItems="center" gap="$300">
          <Icon
            color={iconColor}
            height={24}
            width={24}
            flexShrink={0}
            style={{ flexShrink: 0 }}
          />
          <Text $lg={{ size: "$lg" }}>
            <Text
              $lg={{ size: "$lg", semibold: true }}
              semibold
              color="$fillPrimary"
            >
              {copyInBrandColor}
            </Text>
            {copyInPrimaryColor}
          </Text>
        </XStack>
      ))}
    </Section>
  );

  if (lg) {
    return (
      <YStack width="100%">
        {banner && <View marginTop={headerHeight.current}>{banner}</View>}
        <XStack
          maxWidth="$landingPageConstraint"
          alignSelf="center"
          paddingHorizontal="$250"
          gap="$800"
          justifyContent="center"
          paddingVertical={140}
          width="100%"
        >
          <YStack flex={1}>
            {header}
            {points}
            <Section alignItems="center" gap="$200">
              <Button
                onPress={onPress}
                alignSelf="stretch"
                disabled={disabled}
                dataTrackingId={dataTrackingId}
              >
                <Text
                  zIndex={2}
                  color={
                    disabled ? "$textButtonDisabled" : "$textButtonPrimary"
                  }
                >
                  {ctaCopy}
                </Text>
              </Button>
              {secondaryCtaCopy && onSecondaryPress && (
                <Button
                  mode="secondary"
                  onPress={onSecondaryPress}
                  alignSelf="stretch"
                  disabled={disabled}
                  dataTrackingId={secondaryDataTrackingId}
                >
                  {secondaryCtaCopy}
                </Button>
              )}
              {ctaHelperText && (
                <Paragraph
                  textAlign="center"
                  size="$xs"
                  color="$textSecondary"
                  testID="cta-helper-text"
                >
                  {ctaHelperText}
                </Paragraph>
              )}
              <Paragraph textAlign="center" size="$xs" color="$textPrimary">
                By continuing, you agree to our{" "}
                <Link size="$xs" href="/terms" bold>
                  Terms
                </Link>{" "}
                and{" "}
                <Link size="$xs" href="/privacy" bold>
                  Privacy Policy
                </Link>
                .
              </Paragraph>
            </Section>
          </YStack>
          <Square
            size={350}
            $xl={{ size: 470 }}
            alignSelf="center"
            borderRadius="$150"
            overflow="hidden"
            shadowRadius="$400"
            shadowOpacity={0.3}
            shadowColor="$shadowButton"
            shadowOffset={{
              width: 0,
              height: 4,
            }}
          >
            <Image
              width="100%"
              height="100%"
              objectFit="cover"
              source={heroImage}
            />
          </Square>
        </XStack>
      </YStack>
    );
  }

  return (
    <YStack alignSelf="center" paddingHorizontal="$250" paddingTop="$500">
      <Logo alignSelf="center" marginBottom="$400" />
      {banner && (
        <View marginHorizontal="$-250" marginBottom="$400">
          {banner}
        </View>
      )}
      {header}
      {points}
      <Section alignItems="center" gap="$200">
        <Button
          onPress={onPress}
          disabled={disabled}
          alignSelf="stretch"
          dataTrackingId={dataTrackingId}
        >
          {ctaCopy}
        </Button>
        {secondaryCtaCopy && onSecondaryPress && (
          <Button
            mode="secondary"
            onPress={onSecondaryPress}
            disabled={disabled}
            alignSelf="stretch"
            dataTrackingId={secondaryDataTrackingId}
          >
            {secondaryCtaCopy}
          </Button>
        )}
        {ctaHelperText && (
          <Paragraph
            textAlign="center"
            size="$xs"
            color="$textSecondary"
            testID="cta-helper-text"
          >
            {ctaHelperText}
          </Paragraph>
        )}
        <Paragraph
          textAlign="center"
          size="$xs"
          color="$textPrimary"
          marginBottom="$300"
        >
          By continuing, you agree to our{" "}
          <Link size="$xs" href="/terms" bold>
            Terms
          </Link>{" "}
          and{" "}
          <Link size="$xs" href="/privacy" bold>
            Privacy Policy
          </Link>
          .
        </Paragraph>
      </Section>
      <Image
        marginHorizontal="$-250"
        width="100vw"
        aspectRatio={heroAspectRatio}
        maxHeight={400}
        objectFit="cover"
        source={heroImage}
        onLoad={onHeroLoad}
      />
    </YStack>
  );
};

const GLP1HeroWithCTA = () => {
  const router = usePropagatedRouter();
  const { mutate: createEligibilityConversation, isPending } =
    useCreateRxEligibilityConversation();
  const { mutate: postNoriMessage } = usePostNoriMessage();
  const { open: openRxQuestionnaire } = useRxQuestionnaireStore();
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { setModalContent } = useContext(LayoutContext);
  const { showWaitTimeBanner, waitTimeCopy } = useWaitTimeBanner();

  return (
    <HeroWithCTA
      ctaCopy="Check eligibility"
      onPress={async () => {
        const partnerIntent = await getLocalPartnerIntent();
        createEligibilityConversation(
          {
            data: {
              initial_message: "I'm interested in medication for weight loss",
              partner_intent: partnerIntent ?? null,
            },
          },
          {
            onSuccess: ({ conversation_id }) => {
              track(AD_CONVERSION_EVENTS.conversationCreated, {
                event_context: {
                  ...eventContext,
                  conversation_id,
                  conversation_type: "nori",
                },
              });
              router.navigate(`/conversation/${conversation_id}`);
              postNoriMessage({
                message: "Yes",
                conversationId: conversation_id,
                type: SendMessageRequestType.rx_input,
              });
              setTimeout(() => {
                openRxQuestionnaire(conversation_id);
              }, 300);
            },
          },
        );
      }}
      secondaryCtaCopy="Refill existing GLP-1 prescription"
      onSecondaryPress={() => {
        setModalContent(() => () => (
          <AIDoctorIntakeFlow
            type={WorkflowType.rx_renewal}
            onClose={() => setModalContent(null)}
          />
        ));
      }}
      secondaryDataTrackingId="glp1-refill-existing-prescription"
      banner={
        showWaitTimeBanner && <WaitTimeBanner waitTimeCopy={waitTimeCopy} />
      }
      disabled={isPending}
      heroImage={GLP1Hero}
      header={
        <>
          <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
            Start your journey with{" "}
            <Text
              size="$xxl"
              semibold
              $lg={{ size: "$3xl", semibold: true }}
              italic
              color="$fillPrimary"
            >
              weight loss{" "}
            </Text>
            for free
          </Text>
        </>
      }
      items={[
        {
          Icon: ClipboardCheck,
          copyInBrandColor: "See if you are eligible ",
          copyInPrimaryColor: "within minutes",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "Virtually connect with a doctor ",
          copyInPrimaryColor: "to get the prescription sent to your pharmacy",
        },
        {
          Icon: HealthcareOutline,
          copyInBrandColor: "Supportive follow up care ",
          copyInPrimaryColor: "via text - no downloading an app",
        },
      ]}
    />
  );
};

const TalkToDoctorHeroWithCTA = () => {
  const router = usePropagatedRouter();
  const { sendNoriMessage } = useSendNoriMessage();
  const { setModalContent } = useContext(LayoutContext);
  const { status, conversationId, encounterType } = useVisitStatus();
  const hasInProgressChat =
    status === VISIT_STATUS.OPEN || status === VISIT_STATUS.UNREAD;
  const isPending = status === VISIT_STATUS.PENDING;
  const isResumable = status === VISIT_STATUS.RESUMABLE;
  const hasOngoingVisit = hasInProgressChat || isPending || isResumable;

  // Block starting a NEW visit while an account merge is in progress (returning
  // to an ongoing visit is still fine). Flag-gated so there's no extra
  // /merge-status traffic when the flow is off.
  const { value: isAccountMergeEnabled } = useFeatureFlag(
    "curai-classic-to-chai-account-merge",
    false,
  );
  const { data: mergeStatus, isLoading: isMergeStatusLoading } = useMergeStatus(
    {
      enabled: isAccountMergeEnabled,
    },
  );
  // Also block while the merge status is still loading: until it resolves,
  // patient_facing_status is undefined and we can't rule out an in-progress
  // merge — so keep the start CTA disabled rather than let a mid-merge patient
  // slip a visit through the load window.
  const blockStartForMerge =
    isAccountMergeEnabled &&
    !hasOngoingVisit &&
    (isMergeStatusLoading ||
      mergeStatus?.patient_facing_status === "in_progress");

  const handleReturnToVisit = () => {
    if (!conversationId) return;
    if (isResumable) {
      setModalContent(() => (props: any) => (
        <CuraiConsentRedirect
          resume
          conversationId={conversationId}
          {...props}
        />
      ));
    } else if (isPending) {
      if (encounterType === EncounterStartType.AUDIOVISUAL) {
        setModalContent(() => (props: any) => (
          <VideoPreview curaiConversationId={conversationId} {...props} />
        ));
      } else {
        setModalContent(() => (props: any) => (
          <WaitingRoom
            encounterType={encounterType ?? EncounterStartType.TEXT_BASED}
            curaiConversationId={conversationId}
            {...props}
          />
        ));
      }
    } else {
      router.navigate(`/conversation/${conversationId}`);
    }
  };

  const handleStartVisit = () => {
    if (hasOngoingVisit) {
      handleReturnToVisit();
      return;
    }

    // Don't kick off a new visit while an account merge is in progress.
    if (blockStartForMerge) return;

    sendNoriMessage("", {
      type: SendMessageRequestType.talk_to_doctor,
    });
  };

  return (
    <HeroWithCTA
      ctaCopy={
        hasOngoingVisit ? "Return to your visit" : "Video call with a clinician"
      }
      onPress={handleStartVisit}
      dataTrackingId={
        hasOngoingVisit ? undefined : "talk-to-doctor-clicked-video-button"
      }
      secondaryCtaCopy={hasOngoingVisit ? undefined : "Chat with a clinician"}
      onSecondaryPress={hasOngoingVisit ? undefined : handleStartVisit}
      secondaryDataTrackingId={
        hasOngoingVisit ? undefined : "talk-to-doctor-clicked-chat-button"
      }
      disabled={blockStartForMerge}
      ctaHelperText={
        blockStartForMerge
          ? // TODO(copy): finalize wording with product.
            "Your account merge is in progress. You'll be able to start a visit once it finishes."
          : undefined
      }
      heroImage={TalkToDoctorHero}
      header={
        <>
          <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
            Talk to a doctor online{" "}
            <Text
              size="$xxl"
              semibold
              $lg={{ size: "$3xl", semibold: true }}
              italic
              color="$fillPrimary"
            >
              for free{" "}
            </Text>
            within minutes
          </Text>
        </>
      }
      items={[
        {
          Icon: Clock,
          copyInBrandColor: "Available 24/7 ",
          copyInPrimaryColor: "with no appointment needed",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "Virtually connect with a doctor ",
          copyInPrimaryColor: "for diagnosis and treatment",
        },
        {
          Icon: HealthcareOutline,
          copyInBrandColor: "Supportive follow up care ",
          copyInPrimaryColor: "via text - no downloading an app",
        },
      ]}
    />
  );
};

const MenopauseHeroWithCTA = () => {
  const router = usePropagatedRouter();
  const { mutate: createEligibilityConversation, isPending } =
    useCreateRxEligibilityConversation();
  const { mutate: postNoriMessage } = usePostNoriMessage();
  const { open: openRxQuestionnaire } = useRxQuestionnaireStore();
  const { getLocalPartnerIntent } = usePartnerIntent();

  return (
    <HeroWithCTA
      ctaCopy="Start the quiz"
      onPress={async () => {
        const partnerIntent = await getLocalPartnerIntent();
        createEligibilityConversation(
          {
            data: {
              initial_message: "I'm interested in HRT for menopause symptoms",
              partner_intent: partnerIntent ?? null,
            },
          },
          {
            onSuccess: ({ conversation_id }) => {
              router.navigate(`/conversation/${conversation_id}`);
              postNoriMessage({
                message: "Yes",
                conversationId: conversation_id,
                type: SendMessageRequestType.rx_input,
              });
              setTimeout(() => {
                openRxQuestionnaire(conversation_id);
              }, 300);
            },
          },
        );
      }}
      disabled={isPending}
      heroImage={MenopauseHero}
      header={
        <>
          <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
            <Text
              size="$xxl"
              semibold
              $lg={{ size: "$3xl", semibold: true }}
              italic
              color="$fillPrimary"
            >
              Get HRT{" "}
            </Text>
            to relieve your menopause symptoms
          </Text>
        </>
      }
      items={[
        {
          Icon: ClipboardCheck,
          copyInBrandColor: "See if you are eligible ",
          copyInPrimaryColor: "within minutes",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "Virtually connect with a doctor ",
          copyInPrimaryColor: "to get the prescription sent to your pharmacy",
        },
        {
          Icon: HealthcareOutline,
          copyInBrandColor: "Supportive follow up care ",
          copyInPrimaryColor: "via text - no downloading an app",
        },
      ]}
    />
  );
};

const SymptomCheckingHeroWithCTA = () => {
  const { sendNoriMessage } = useSendNoriMessage();

  return (
    <HeroWithCTA
      ctaCopy="Check symptoms now"
      onPress={() => {
        sendNoriMessage("Check symptoms now");
      }}
      disabled={false}
      heroImage={SymptomCheckingHero}
      header={
        <>
          <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
            Get answers about{" "}
            <Text
              size="$xxl"
              semibold
              $lg={{ size: "$3xl", semibold: true }}
              italic
              color="$fillPrimary"
            >
              your symptoms{" "}
            </Text>
            for free
          </Text>
        </>
      }
      items={[
        {
          Icon: ConversationText2Outline,
          copyInBrandColor: "Answer a few questions ",
          copyInPrimaryColor: "about what you're experiencing",
        },
        {
          Icon: IdeaOutline,
          copyInBrandColor: "Get clarity on your symptoms ",
          copyInPrimaryColor: "and recommended next steps",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "Connect to a doctor ",
          copyInPrimaryColor: "within minutes if needed",
        },
      ]}
    />
  );
};

const UTIReliefHeroWithCTA = () => {
  const { sendNoriMessage } = useSendNoriMessage();

  return (
    <HeroWithCTA
      ctaCopy="Get UTI relief"
      onPress={() => {
        sendNoriMessage("UTI symptom checker");
      }}
      disabled={false}
      heroImage={UTIReliefHero}
      header={
        <>
          <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
            Get{" "}
            <Text
              size="$xxl"
              semibold
              $lg={{ size: "$3xl", semibold: true }}
              italic
              color="$fillPrimary"
            >
              fast relief{" "}
            </Text>
            from UTI symptoms
          </Text>
        </>
      }
      items={[
        {
          Icon: ConversationText2Outline,
          copyInBrandColor: "Answer a few questions ",
          copyInPrimaryColor: "about what you're experiencing",
        },
        {
          Icon: IdeaOutline,
          copyInBrandColor: "Get clarity on your symptoms ",
          copyInPrimaryColor: "and recommended next steps",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "Connect to a doctor ",
          copyInPrimaryColor: "within minutes if needed",
        },
      ]}
    />
  );
};

export const OnlinePrescriptionRefillHeroWithCTA = () => {
  const { setModalContent } = useContext(LayoutContext);
  const onClose = () => setModalContent(null);

  const handleRefillPress = () => {
    setModalContent(() => () => (
      <AIDoctorIntakeFlow type={WorkflowType.rx_renewal} onClose={onClose} />
    ));
  };

  return (
    <HeroWithCTA
      header={
        <Text size="$xxl" semibold $lg={{ size: 40, semibold: true }}>
          Refill your prescription online in minutes{" "}
          <Text
            size="$xxl"
            $lg={{ size: 40, semibold: true }}
            color="$textLink"
            display="inline"
            semibold
            italic
          >
            for free
          </Text>
        </Text>
      }
      items={[
        {
          Icon: ClipboardCheck,
          copyInBrandColor: "",
          copyInPrimaryColor:
            "Create an account and share your medication information",
        },
        {
          Icon: CheckSquare,
          copyInBrandColor: "",
          copyInPrimaryColor: "Answer a few quick questions",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "",
          copyInPrimaryColor:
            "A licensed clinician sends your refill to your pharmacy",
        },
      ]}
      ctaCopy="Get my free refill"
      heroImage={AppImage}
      onPress={handleRefillPress}
      disabled={false}
      dataTrackingId="get-my-free-renewal"
    />
  );
};

export const DiagnosisHeroWithCTA = () => {
  const { setModalContent } = useContext(LayoutContext);
  const onClose = () => setModalContent(null);

  const handleButtonPress = () => {
    setModalContent(() => () => (
      <AIDoctorIntakeFlow type={WorkflowType.diagnosis} onClose={onClose} />
    ));
  };

  return (
    <HeroWithCTA
      header={
        <Text size="$xxl" semibold $lg={{ size: 40, semibold: true }}>
          Get treatment in minutes{" "}
          <Text
            size="$xxl"
            $lg={{ size: 40, semibold: true }}
            color="$textLink"
            display="inline"
            semibold
            italic
          >
            for free
          </Text>
        </Text>
      }
      items={[
        {
          Icon: User,
          copyInBrandColor: "",
          copyInPrimaryColor: "Sign up for an account",
        },
        {
          Icon: CheckSquare,
          copyInBrandColor: "",
          copyInPrimaryColor: "Answer a few questions about your symptoms",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "",
          copyInPrimaryColor:
            "A licensed clinician reviews your case and handles treatment if appropriate",
        },
      ]}
      ctaCopy="Get started"
      heroImage={AppImage}
      onPress={handleButtonPress}
      disabled={false}
      dataTrackingId="get-started-with-diagnosis"
    />
  );
};

const OnlineUTITreatmentHeroWithCTA = () => {
  const { setModalContent } = useContext(LayoutContext);
  const onClose = () => setModalContent(null);

  const handleButtonPress = () => {
    setModalContent(() => () => (
      <AIDoctorIntakeFlow type={WorkflowType.diagnosis} onClose={onClose} />
    ));
  };

  return (
    <HeroWithCTA
      ctaCopy="Get your free treatment"
      onPress={handleButtonPress}
      disabled={false}
      heroImage={UTIReliefHero}
      header={
        <YStack gap="$300" marginBottom="$200">
          <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
            Free UTI treatment without the waiting room
          </Text>
          <Text color="$textSecondary">
            Answer a few questions. A doctor reviews your case in under an hour
            — while you get on with your day.
          </Text>
        </YStack>
      }
      items={[
        {
          Icon: ConversationText2Outline,
          copyInBrandColor: "Answer a few questions ",
          copyInPrimaryColor: "about your symptoms and medical history",
        },
        {
          Icon: DoctorOutline,
          copyInBrandColor: "A board-certified clinician ",
          copyInPrimaryColor: "reviews your case within an hour",
        },
        {
          Icon: PillBottleWithPill,
          copyInBrandColor: "Prescription sent ",
          copyInPrimaryColor: "to pharmacy - you only pay for the medication",
        },
      ]}
    />
  );
};

export const HERO_BY_USE_CASE: Partial<Record<UseCase, React.ReactNode>> = {
  "medical-treatments-for-weight-loss": <GLP1HeroWithCTA />,
  "talk-to-a-doctor": <TalkToDoctorHeroWithCTA />,
  "medical-treatments-for-menopause": <MenopauseHeroWithCTA />,
  "symptom-checking": <SymptomCheckingHeroWithCTA />,
  "uti-relief": <UTIReliefHeroWithCTA />,
  "online-uti-treatment": <OnlineUTITreatmentHeroWithCTA />,
  "online-prescription-refill": <OnlinePrescriptionRefillHeroWithCTA />,
  "free-diagnosis-online": <DiagnosisHeroWithCTA />,
};
